import React, { useState } from "react";
import { bool, string } from "prop-types";
import { twMerge } from "tailwind-merge";
import { useTranslation } from "@lib/i18n";

import { Button } from "@components/shared/Button";
import ShareIcon from "@components/shared/Icons/ShareIcon";
import { useSetWithdrawalModalOpenContext } from "@components/shared/WithdrawalModal";
import FooterSection from "./FooterSection/FooterSection";
import LeadFormButton from "./LeadFormButton";
import SaveProgress from "./SaveProgress";
import SellerInfoModal from "./SellerInfoModal";
import ShareInfoModal from "./ShareInfoModal";

import gtm from "@gtm/core";
import { openDocument } from "@lib/openDocument";
import { COUNTRIES, PDF_DOCUMENT_TYPES } from "@shared/constants";
import { useGetAssetPath } from "hooks/useGetAssetPath";

import {
  useSolJourneySelector,
  useIsLOAJourneyEnabledSelector,
  useIsLLDJourneyEnabledSelector,
  useIsVACJourneyEnabledSelector,
  useWithdrawalSettingsSelector,
} from "@redux/reducers/appConfig";

import {
  displayCookiesEvent,
  displayTermsOfServiceEvent,
  displayTermsOfSalesEvent,
  displayServiceConditionsEvent,
  displayLeasingTermsOfSalesEvent,
  displaySellerInformationEvent,
  displayShareInformationEvent,
  displayAccessibilityEvent,
  displayTermsOfAssistance,
  displayTermsOfWarranty,
} from "@gtm/events/shared";
import { getIsCountry } from "lib/getIsCountry";

const NavigationButton = ({ className, ...restProps }) => (
  <button
    className={twMerge(
      "text-white text-sm relative cursor-pointer px-2 max-md:rounded-lg",
      "max-md:py-1 md:pr-4",
      "hover:underline",
      className
    )}
    role="button"
    {...restProps}
  />
);

NavigationButton.propTypes = {
  className: string,
};

const Footer = ({ withSave, saveProgressSource, leadFormLink }) => {
  const { t, i18n } = useTranslation();
  const [isSellerInfoOpen, setIsSellerInfoOpen] = useState(false);
  const [isShareInfoOpen, setIsShareInfoOpen] = useState(false);
  const getAssetPath = useGetAssetPath(i18n.language);

  const { accessibilityDeclaration } = useSolJourneySelector();
  const isLOAJourneyEnabled = useIsLOAJourneyEnabledSelector();
  const isLLDJourneyEnabled = useIsLLDJourneyEnabledSelector();
  const isVACJourneyEnabled = useIsVACJourneyEnabledSelector();

  const withdrawalSettings = useWithdrawalSettingsSelector();
  const setWithdrawalModalOpen = useSetWithdrawalModalOpenContext();

  const handleCookieClick = () => {
    gtm.fire(displayCookiesEvent());

    const MODAL_ELEMENTS_SELECTOR = "#_psaihm_overlay, #_psaihm_main_div";
    const DISPLAY_CLASS_NAME = "_psaihm_css_collapse";

    document.querySelectorAll(MODAL_ELEMENTS_SELECTOR).forEach(element => {
      element.className = DISPLAY_CLASS_NAME;
      element.style.display = "block";
      // If it's a cookie DIV container, then overwrite transform property.
      if (element.id === "_psaihm_main_div") {
        element.style.transform = "translate(-50%, -50%)";
      }
    });
  };

  const openSellerInformation = () => {
    setIsSellerInfoOpen(true);
    gtm.fire(displaySellerInformationEvent());
  };

  const openShareInfoModal = () => {
    setIsShareInfoOpen(true);
    gtm.fire(displayShareInformationEvent());
  };

  return (
    <footer className="flex flex-col bg-primaryLight" data-id="page-footer">
      {withSave && <SaveProgress updateSource={saveProgressSource} />}
      {leadFormLink && <LeadFormButton link={leadFormLink} />}

      <FooterSection />

      <SellerInfoModal
        isOpen={isSellerInfoOpen}
        closeModal={() => setIsSellerInfoOpen(false)}
      />

      <ShareInfoModal
        isOpen={isShareInfoOpen}
        closeModal={() => setIsShareInfoOpen(false)}
      />
      <div className="flex flex-col justify-center items-center gap-2 bg-primaryDark text-white md:gap-y-4 md:px-4">
        <div className="flex flex-col flex-wrap justify-center items-center gap-2 p-8 min-h-20 md:flex-row md:min-h-32 md:p-4 md:divide-x">
          <NavigationButton
            onClick={() => {
              gtm.fire(displayTermsOfServiceEvent());
              if (getIsCountry(COUNTRIES.GB)) {
                window.open(t("footer.termsOfServiceLink"), "_blank");
              } else {
                openDocument(getAssetPath(PDF_DOCUMENT_TYPES.TERMS_OF_SERVICE));
              }
            }}
          >
            {t("footer.termsOfService")}
          </NavigationButton>

          <NavigationButton
            onClick={() => {
              gtm.fire(displayTermsOfSalesEvent());
              openDocument(
                getAssetPath(PDF_DOCUMENT_TYPES.TERMS_OF_SALES_CASH)
              );
            }}
          >
            {t("footer.termsOfSales")}
          </NavigationButton>

          <NavigationButton
            onClick={() => {
              gtm.fire(displayServiceConditionsEvent());
              openDocument(getAssetPath(PDF_DOCUMENT_TYPES.AMI_CARE_SERVICE));
            }}
          >
            {t("footer.serviceConditions")}
          </NavigationButton>

          {isLLDJourneyEnabled && (
            <NavigationButton
              onClick={() => {
                gtm.fire(displayLeasingTermsOfSalesEvent());
                openDocument(
                  getAssetPath(PDF_DOCUMENT_TYPES.TERMS_OF_SALES_LLD)
                );
              }}
            >
              {t("footer.deliveryGuaranteeConditions")}
            </NavigationButton>
          )}

          {isLOAJourneyEnabled && (
            <NavigationButton
              onClick={() => {
                openDocument(
                  getAssetPath(PDF_DOCUMENT_TYPES.TERMS_OF_SALES_LOA)
                );
              }}
            >
              {t("footer.loaConditions")}
            </NavigationButton>
          )}

          {isVACJourneyEnabled && t("footer.vacConditions") && (
            <NavigationButton
              onClick={() => {
                openDocument(
                  getAssetPath(PDF_DOCUMENT_TYPES.TERMS_OF_SALES_VAC)
                );
              }}
            >
              {t("footer.vacConditions")}
            </NavigationButton>
          )}

          {t("footer.makeComplaints.link") &&
            t("footer.makeComplaints.heading") && (
              <NavigationButton
                onClick={() =>
                  window.open(t("footer.makeComplaints.link"), "_blank")
                }
              >
                {t("footer.makeComplaints.heading")}
              </NavigationButton>
            )}

          <NavigationButton onClick={openSellerInformation}>
            {t("footer.sellerInformation")}
          </NavigationButton>

          {accessibilityDeclaration.isRequired && (
            <NavigationButton
              onClick={() => {
                gtm.fire(displayAccessibilityEvent());
                getIsCountry(COUNTRIES.GB)
                  ? window.open(t("footer.accessibilityLink"), "_blank")
                  : openDocument(
                      getAssetPath(PDF_DOCUMENT_TYPES.ACCESSIBILITY_DECLARATION)
                    );
              }}
            >
              {t("footer.accessibility")}
            </NavigationButton>
          )}

          {t("footer.termsOfAssistance") &&
            t("footer.termsOfAssistance").length > 0 && (
              <NavigationButton
                onClick={() => {
                  gtm.fire(displayTermsOfAssistance());
                  openDocument(
                    getAssetPath(PDF_DOCUMENT_TYPES.TERMS_OF_ASSISTANCE)
                  );
                }}
              >
                {t("footer.termsOfAssistance")}
              </NavigationButton>
            )}

          {t("footer.termsOfWarranty") &&
            t("footer.termsOfWarranty").length > 0 && (
              <NavigationButton
                onClick={() => {
                  gtm.fire(displayTermsOfWarranty());
                  openDocument(
                    getAssetPath(PDF_DOCUMENT_TYPES.TERMS_OF_WARRANTY)
                  );
                }}
              >
                {t("footer.termsOfWarranty")}
              </NavigationButton>
            )}

          <NavigationButton
            onClick={handleCookieClick}
            data-id="footer-cookies-preferences"
          >
            {t("cookies.footerTitle")}
          </NavigationButton>

          {getIsCountry(COUNTRIES.GB) && (
            <NavigationButton
              onClick={() => {
                window.open(t("footer.privacyPolicyLink"), "_blank");
              }}
            >
              {t("footer.privacyPolicy")}
            </NavigationButton>
          )}

          <Button
            className="text-xs md:ml-2"
            onClick={openShareInfoModal}
            size="small"
            variant="primary"
          >
            {t("footer.share")}
            <ShareIcon data-id="footer-share-icon" width="24px" height="24px" />
          </Button>
        </div>
        {withdrawalSettings.enabled && (
          <div className="flex justify-center items-center gap-2 p-8 border-t border-t-white w-full md:p-4 md:pb-0">
            <button
              className="uppercase text-sm  font-semibold cursor-pointer hover:bg-primaryDarkAccent px-4 py-2 rounded-md transition duration-300 hover:scale-105 active:scale-95"
              onClick={() => setWithdrawalModalOpen(true)}
            >
              {t("footer.withdrawFromOrder")}
            </button>
          </div>
        )}
        <div className="flex justify-center items-center gap-2 p-8 border-t border-t-white w-full md:p-4">
          {t("footer.legalMention") && (
            <p className="text-sm text-center">{t("footer.legalMention")}</p>
          )}
        </div>
      </div>
    </footer>
  );
};

Footer.propTypes = {
  leadFormLink: string,
  withSave: bool,
  saveProgressSource: string,
};

export default React.memo(Footer);
